import React, { memo, useCallback, useContext, useEffect, useState } from 'react'
import Navbar from '../../../components/Navbar'
import { connect } from 'react-redux'
import setRedirectUrl from '../../../redux/actions/redirection/setRedirectUrl'
import SearchIcon from '@mui/icons-material/Search'
import { useMediaQuery, useTheme, styled, Box, Button, Typography, IconButton, CircularProgress } from '@mui/material'
import Footer from '../../Home/components/Footer'
import useRubros from '../../Products/hooks/rubros.hook'
import DesplegableRubros from '../../AdminProducts/ABMProducts/components/DesplegableRubros'
import { TenantsContext } from '../../../context/tenants.context'
import { entrenarModelo, getFotosEntrenamiento, statusEntrenamiento } from '../../AdminProducts/services/rubros.service'
import { defaultPaginatorRes, PaginatorRes } from '../../Pedidos/context/pedidos.context'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import ListadoFotos from './listadoFotos'
import { INITIAL_ROWS_PER_PAGE } from '../../Products/components/TableDataPagination'
import NuevaFoto from './nuevaFoto'
import ConfirmDialog from '../../Products/components/ConfirmDialog'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import DialogStatusJob from './dialogStatusJob'
import InfoIcon from '@mui/icons-material/Info'

export const Header = styled('div')({
  display: 'flex',
  justifyContent: 'space-between'
})

export interface IEntrenamiento {
  idIaEntrenamientoFoto: number
  url: string
  codRubros: string[]
}

export interface IStatusJob {
  jobId: string
  jobName: string
  status: string
  statusReason: string
  createdAt: number
  startedAt: number
  stoppedAt: number
  durationSeconds: number
}

const EntrenamientoIA: React.FC = (props: any) => {
  const { flatRubrosHijos } = useRubros()
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false)
  const [fotos, setFotos] = useState<IEntrenamiento[]>(null)
  const [codRubro, setCodRubro] = useState<string>(null)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [paginator, setPaginator] = useState<PaginatorRes>(defaultPaginatorRes)
  const [newFoto, setNewFoto] = useState<boolean>(false)
  const [statusJob, setStatusJob] = useState<IStatusJob>(null)
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { tenant } = useContext(TenantsContext)
  const [confirmDialog, setConfirmDialog] = useState<any>({
    open: false,
    text: {
      title: '',
      body: '',
      leftButton: 'Cancelar',
      rightButton: 'Confirmar',
      colorButtonConfirm: 'primary'
    },
    action: async () => {}
  })

  const clearConfirmDialog = () => {
    setConfirmDialog((confirmDialog: any) => ({
      ...confirmDialog,
      open: false
    }))
  }

  const handleEntrenar = useCallback(() => {
    setConfirmDialog({
      open: true,
      text: {
        title: 'Entrenar modelo',
        body: '¿Desea continuar?',
        leftButton: 'Cancelar',
        rightButton: 'Confirmar',
        colorButtonConfirm: 'error'
      },
      action: async () => {
        try {
          setLoading(true)
          const res = await entrenarModelo(tenant)
          res.data.data = SnackBarUtils.success(`Se creó el job de entrenamiento ${res.data.data}`)
          fetchStatusJob()
        } catch (e) {
          console.log(e)
          SnackBarUtils.error(
            e.message === 'Network Error' || !e.message ? 'Ocurrió un error al entrenar modelo' : e.message
          )
        } finally {
          setLoading(false)
          clearConfirmDialog()
        }
      },
      close: () => clearConfirmDialog()
    })
  }, [tenant])

  const fetchFotos = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getFotosEntrenamiento(tenant, codRubro, paginator.pageNumber, paginator.pageSize)
      setFotos(res.data.data)
      setPaginator({
        ...paginator,
        pageNumber: res.data.pageNumber,
        pageSize: res.data.pageSize,
        totalRecord: res.data.totalRecord,
        hasNextPage: res.data.hasNextPage
      })
    } catch (e) {
      console.log(e)
      SnackBarUtils.error(
        e.message === 'Network Error' || !e.message ? 'Ocurrió un error al obtener las imágenes' : e.message
      )
    } finally {
      setLoading(false)
    }
  }, [codRubro, paginator, tenant])

  const fetchStatusJob = useCallback(async () => {
    try {
      setLoadingStatus(true)
      const res = await statusEntrenamiento(tenant)
      setStatusJob(res.data.data)
    } catch (e) {
      console.log(e)
      SnackBarUtils.error(
        e.message === 'Network Error' || !e.message ? 'Ocurrió un error al obtener el estado del último Job' : e.message
      )
    } finally {
      setLoadingStatus(false)
    }
  }, [tenant])

  useEffect(() => {
    fetchFotos()
    fetchStatusJob()
  }, [tenant])

  useEffect(() => {
    fetchFotos()
  }, [page, pageSize])

  paginator.handleChangePage = (e: any, newPage: number) => {
    setPage(newPage + 1)
    setPaginator({ ...paginator, pageNumber: newPage + 1 })
  }

  paginator.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newPageSize = parseInt(event.target.value, 10)
    setPageSize(parseInt(event.target.value, 10))
    setPaginator({ ...paginator, pageSize: newPageSize, pageNumber: 0 })
  }

  return (
    <>
      <Navbar title="ABM Entrenamiento" />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          width: '100%',
          maxWidth: '100%',
          gap: 1,
          p: 2,
          boxSizing: 'border-box' // evita overflow horizontal
        }}
      >
        {/* Desplegable: ocupa más espacio */}
        <Box sx={{ flexGrow: 1, flexShrink: 1 }}>
          <DesplegableRubros
            disabled={loading}
            rubros={flatRubrosHijos}
            onChange={(r: any) => {
              setCodRubro(r?.codRubro || null)
              setPaginator({ ...paginator, pageNumber: 0 })
            }}
            handleFormSubmit={fetchFotos}
          />
        </Box>
        {/* Botones: ocupan solo lo necesario, pero en mobile se expanden */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Button
            disabled={loading}
            variant="contained"
            size="large"
            onClick={fetchFotos}
            //fullWidth
            sx={{
              height: 56,
              '@media (max-width: 768px)': {
                display: 'flex',
                gap: '10px'
              }
            }}
          >
            <Box
              sx={{
                display: 'none',
                '@media (max-width: 768px)': {
                  display: 'block'
                }
              }}
            >
              Filtrar
            </Box>
            <SearchIcon style={{ margin: 0 }} />
          </Button>
          <Button
            variant="contained"
            disabled={loading}
            color="success"
            sx={{
              height: 56,
              width: { xs: '100%', sm: 'auto' }
            }}
            onClick={() => setNewFoto(true)}
          >
            Nueva Foto
          </Button>
          <Button
            variant="outlined"
            disabled={loading}
            color="success"
            sx={{
              height: 56,
              width: { xs: '100%', sm: 'auto' }
            }}
            onClick={handleEntrenar}
          >
            Entrenar modelo
          </Button>
          {loadingStatus ? (
            <CircularProgress sx={{ margin: '0.5rem' }} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                height: 56
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Status: {statusJob?.status}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statusJob?.startedAt && format(statusJob.startedAt, 'dd/MM/yyyy HH:mm', { locale: es })}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setStatusDialogOpen(true)} color="primary">
                <InfoIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>

      <ListadoFotos
        fotos={fotos}
        paginator={paginator}
        rubros={flatRubrosHijos}
        loading={loading}
        setLoading={setLoading}
        updateData={fetchFotos}
        isMobile={isMobile}
      />
      <Footer />
      <NuevaFoto
        open={newFoto}
        setOpen={setNewFoto}
        rubros={flatRubrosHijos}
        loading={loading}
        setLoading={setLoading}
        isMobile={isMobile}
        updateData={fetchFotos}
      />
      {statusJob && <DialogStatusJob open={statusDialogOpen} setOpen={setStatusDialogOpen} statusJob={statusJob} />}
      <ConfirmDialog {...confirmDialog} />
    </>
  )
}

const mapStateToProps = (state: any) => {
  return {
    logged: state.logged,
    theme: state.theme,
    toaster: state.toaster,
    redirection: state.redirection
  }
}

const mapDispatchToProps = {
  // resetToaster,
  setRedirectUrl
}

export default connect(mapStateToProps, mapDispatchToProps)(memo(EntrenamientoIA))
